import { Share2, Download, Twitter, Facebook, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { toPng } from "html-to-image";
import { addWatermark } from "@/lib/watermark";
import { AUTH_DISABLED, IMAGE_SHARING_ENABLED } from "@/lib/featureFlags";
import { useAuth } from "@/contexts/AuthContext";

interface QuickShareButtonProps {
  elementRef: React.RefObject<HTMLElement>;
  title: string;
  fileName: string;
  shareText?: string;
  variant?: "outline" | "ghost";
  size?: "sm" | "icon" | "default";
  iconOnly?: boolean;
}

export const QuickShareButton = ({
  elementRef,
  title,
  fileName,
  shareText,
  variant = "outline",
  size = "sm",
  iconOnly = false,
}: QuickShareButtonProps) => {
  const { user, isPremium } = useAuth();
  const { toast } = useToast();

  const generateImage = async (): Promise<Blob | null> => {
    if (!elementRef.current) {
      toast({ title: "Error", description: "Unable to generate image.", variant: "destructive" });
      return null;
    }

    try {
      let dataUrl = await toPng(elementRef.current, { quality: 1, pixelRatio: 2 });
      dataUrl = await addWatermark(dataUrl, isPremium);
      const response = await fetch(dataUrl);
      return await response.blob();
    } catch (error) {
      console.error("Error generating image:", error);
      toast({ title: "Error", description: "Failed to generate image.", variant: "destructive" });
      return null;
    }
  };

  const handleDownload = async () => {
    const blob = await generateImage();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `${fileName}.png`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded!", description: "Image saved successfully." });
  };

  const handleCopyToClipboard = async () => {
    const blob = await generateImage();
    if (!blob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast({ title: "Copied!", description: "Image copied to clipboard." });
    } catch {
      toast({ title: "Error", description: "Failed to copy image.", variant: "destructive" });
    }
  };

  const handleShareTwitter = () => {
    const text = shareText || `Check out ${title}! #Football #ManagerTable`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleShareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast({ title: "Copied!", description: "Link copied to clipboard." });
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      toast({ title: "Not supported", description: "Your browser doesn't support native sharing.", variant: "destructive" });
      return;
    }
    if (!IMAGE_SHARING_ENABLED) {
      try {
        await navigator.share({ title, url: window.location.href });
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          toast({ title: "Error", description: "Failed to share.", variant: "destructive" });
        }
      }
      return;
    }
    const blob = await generateImage();
    if (!blob) return;
    const file = new File([blob], `${fileName}.png`, { type: "image/png" });
    try {
      await navigator.share({ title, files: [file], url: window.location.href });
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        toast({ title: "Error", description: "Failed to share image.", variant: "destructive" });
      }
    }
  };

  // Auth gate: show disabled button with tooltip for non-logged-in users
  if (!user && !AUTH_DISABLED) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant={variant} size={size} disabled className="opacity-50" aria-label={iconOnly ? "Share" : undefined}>
              <Share2 className={iconOnly ? "h-4 w-4" : "mr-2 h-4 w-4"} />
              {!iconOnly && "Share"}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Sign in to share</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} aria-label={iconOnly ? "Share" : undefined}>
          <Share2 className={iconOnly ? "h-4 w-4" : "mr-2 h-4 w-4"} />
          {!iconOnly && "Share"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {IMAGE_SHARING_ENABLED && (
          <>
            <DropdownMenuItem onClick={handleDownload}>
              <Download className="mr-2 h-4 w-4" />
              Download Image
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCopyToClipboard}>
              <Copy className="mr-2 h-4 w-4" />
              Copy to Clipboard
            </DropdownMenuItem>
          </>
        )}
        {!IMAGE_SHARING_ENABLED && (
          <DropdownMenuItem onClick={handleCopyLink}>
            <Copy className="mr-2 h-4 w-4" />
            Copy Link
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleShareTwitter}>
          <Twitter className="mr-2 h-4 w-4" />
          Share on Twitter
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleShareFacebook}>
          <Facebook className="mr-2 h-4 w-4" />
          Share on Facebook
        </DropdownMenuItem>
        {typeof navigator !== "undefined" && navigator.share && (
          <DropdownMenuItem onClick={handleNativeShare}>
            <Share2 className="mr-2 h-4 w-4" />
            Share...
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
